import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take, timeout, catchError } from 'rxjs/operators';
import { firstValueFrom, throwError } from 'rxjs';
import { registerRequiredRequests } from '../../store/app-init/app-init.actions';
import { selectIsAppInitialized } from '../../store/app-init/app-init.selectors';
import { loadDomains } from '../../store/entities/domains/domains.actions';
import { loadUser } from '../../store/entities/user/user.actions';
import { loadProfile } from '../../store/entities/profile/profile.actions';
import { Auth0Service } from '../auth/services/auth0.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private store = inject(Store);
  private auth0Service = inject(Auth0Service);
  private readonly INITIALIZATION_TIMEOUT = 10000;

  async initialize(
    requiredRequests: string[],
    timeoutMs: number = this.INITIALIZATION_TIMEOUT
  ): Promise<void> {
    await this.auth0Service.init();

    // Unauthenticated after init means a login redirect is already in flight;
    // firing the data loads now would send tokenless requests that 401.
    if (!this.auth0Service.isAuthenticated()) {
      return;
    }

    this.store.dispatch(registerRequiredRequests({ requestKeys: requiredRequests }));

    // Dispatch load actions for required requests
    // Each entity module handles its own loading logic independently
    if (requiredRequests.includes('domains')) {
      this.store.dispatch(loadDomains());
    }

    if (requiredRequests.includes('user')) {
      this.store.dispatch(loadUser());
    }

    if (requiredRequests.includes('profile')) {
      this.store.dispatch(loadProfile());
    }

    try {
      await firstValueFrom(
        this.store.select(selectIsAppInitialized).pipe(
          filter((isInitialized) => isInitialized),
          take(1),
          timeout(timeoutMs),
          catchError((error) => {
            if (error.name === 'TimeoutError') {
              throw new Error(
                `App initialization timed out after ${timeoutMs}ms. Required requests: ${requiredRequests.join(', ')}`
              );
            }

            return throwError(() => error);
          })
        )
      );
    } catch (error) {
      console.error('App initialization failed:', error);
      throw error;
    }
  }
}

